/**
 * FileViewerDialog — the lightbox for a message's files and a moment's frame:
 * the file centred over a blurred page, a caption, then Go to frame (a frame)
 * or Download (a file) and Close. ← / → step through the items.
 */
import { lazy, Suspense, type KeyboardEvent, type ReactNode } from 'react';

import { CaretLeft, CaretRight, CircleNotch, type Icon } from '../../../../design-system/icons';
import { formatBytes } from '../../../../lib/format';
import { cn } from '../../../../lib/utils';
import { AppErrorBoundary } from '../../AppErrorBoundary';
import { Dialog, DialogClose, DialogContent, DialogTitle } from '../../../ui/dialog';
import { extensionForContentType, hasKnownSize } from '../fileKinds';
import { fetchableFileId, NO_FILE_ID, type FileSource, type FileSourceResult } from '../fileSource';
import { DownloadLink, FileCard, FileLoadFailedCard } from './FileCard';
import { ImageView } from './ImageView';
import { PdfView } from './PdfView';
import { VideoView } from './VideoView';
import {
  CAPTION_SEPARATOR,
  CLOSE_LABEL,
  GO_TO_FRAME_LABEL,
  LOADING_FILE_LABEL,
  NEXT_FILE_LABEL,
  PREVIOUS_FILE_LABEL,
  type ViewerItem,
} from './viewerModel';

// docx-preview doesn't load until a document is opened.
const DocxView = lazy(() => import('./DocxView').then((m) => ({ default: m.DocxView })));

const VIEWER_TESTID = 'file-viewer';
const PREVIOUS_STEP = -1;
const NEXT_STEP = 1;
const FIRST_INDEX = 0;
const ARROW_LEFT_KEY = 'ArrowLeft';
const ARROW_RIGHT_KEY = 'ArrowRight';
const STEP_ICON_PX = 20;
const SPINNER_PX = 28;

/** The page behind the lightbox: dimmed and blurred, so the file is the only thing in focus. */
const SCRIM_CLASS = 'bg-black/60 backdrop-blur-[3px]';
/** No box of its own: the file, its caption and its actions float centred on the scrim. */
const CONTENT_CLASS =
  'flex h-[92vh] w-[94vw] max-w-none flex-col items-center gap-3 rounded-none border-0 bg-transparent p-0 text-white shadow-none sm:max-w-none';
const BODY_CLASS = 'relative flex min-h-0 w-full flex-1 items-center justify-center overflow-auto px-14';
const CAPTION_CLASS = 'max-w-full shrink-0 truncate px-4 text-[11.5px] tabular-nums text-white/70';
const ACTIONS_CLASS = 'flex shrink-0 items-center justify-center gap-2 pb-2';
const PILL_BASE_CLASS =
  'inline-flex h-[30px] shrink-0 items-center gap-[7px] whitespace-nowrap rounded-full px-[14px] text-[12.5px] font-medium leading-none shadow-none transition-colors duration-150 disabled:pointer-events-none disabled:opacity-40 motion-reduce:transition-none';
/** The lightbox's main action: a white pill. */
const PRIMARY_PILL_CLASS = cn(PILL_BASE_CLASS, 'border border-white bg-white text-zinc-900 hover:bg-white/90 hover:text-zinc-900');
/** Close: a dark pill outlined in white. */
const SECONDARY_PILL_CLASS = cn(PILL_BASE_CLASS, 'border border-white/30 bg-zinc-900/70 text-white hover:border-white/60 hover:bg-zinc-900');
const STEP_BUTTON_CLASS =
  'absolute top-1/2 flex size-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:cursor-not-allowed disabled:opacity-30';
const PREVIOUS_BUTTON_CLASS = 'left-3';
const NEXT_BUTTON_CLASS = 'right-3';
const SPINNER_CLASS = 'flex items-center justify-center text-white/70';
const SPINNER_ICON_CLASS = 'animate-spin';

export interface FileViewerDialogProps {
  items: ViewerItem[];
  /** The item on screen; null (or an index past the items) keeps the viewer closed. */
  index: number | null;
  /** A step to another item, or null to close (Close, Esc or the scrim). */
  onIndexChange(i: number | null): void;
  /** Absent → only items with a local preview show; the rest get the file card. */
  fileSource?: FileSource;
  /** Seeks the player to a moment's frame; adds Go to frame to the header. */
  onGoToFrame?: () => void;
}

function itemAt(items: ViewerItem[], index: number | null): ViewerItem | undefined {
  if (index === null) return undefined;
  return items[index];
}

/** The index a step lands on, or null past either end — the viewer doesn't wrap. */
function stepFrom(index: number | null, step: number, count: number): number | null {
  if (index === null) return null;
  const target = index + step;
  if (target < FIRST_INDEX || target >= count) return null;
  return target;
}

function hasSeveral(items: ViewerItem[]): boolean {
  return items.length > 1;
}

/** A focused video (or audio) seeks with the arrow keys itself. */
function isMediaElement(target: EventTarget): boolean {
  return target instanceof HTMLMediaElement;
}

/** Alt+← is the browser's Back, and Shift, Ctrl or Cmd with an arrow selects or jumps — none of them step. */
function isModifiedKey(event: KeyboardEvent<HTMLDivElement>): boolean {
  return event.altKey || event.ctrlKey || event.metaKey || event.shiftKey;
}

function nothingToRefetch(): void {
  // A local preview is already in memory: there's no download to try again.
}

/** Nothing fetched: only the item's local preview, if it has one. */
function localFile(item: ViewerItem): FileSourceResult {
  return { url: item.localUrl, isLoading: false, isError: false, retry: nothingToRefetch };
}

/**
 * The file source sets its URL in an effect, so for one commit the bytes can
 * be in while the URL isn't yet: that's still loading, not a file that can't
 * be shown.
 */
function awaitingUrl(fetchId: string, fetched: FileSourceResult): boolean {
  return fetchId !== NO_FILE_ID && !fetched.isError && !fetched.url;
}

function viewerFile(item: ViewerItem, fetchId: string, fetched: FileSourceResult): FileSourceResult {
  if (item.localUrl) return localFile(item);
  if (awaitingUrl(fetchId, fetched)) return { ...fetched, isLoading: true };
  return fetched;
}

/** The name Download saves under: the item's own, or — for a moment's frame — its stem plus the extension of the bytes' type. */
function downloadNameOf(item: ViewerItem, file: FileSourceResult): string {
  if (!item.downloadStem) return item.name;
  return `${item.downloadStem}${extensionForContentType(file.blob?.type)}`;
}

function LoadingSpinner() {
  return (
    <div role="status" aria-label={LOADING_FILE_LABEL} className={SPINNER_CLASS}>
      <CircleNotch size={SPINNER_PX} className={SPINNER_ICON_CLASS} aria-hidden />
    </div>
  );
}

interface DocumentBodyProps {
  fileId: string;
  name: string;
  blob?: Blob;
  fallback: ReactNode;
}

/** A failed chunk load (or render) shows the file card rather than taking the dialog down. */
function DocumentBody({ fileId, name, blob, fallback }: DocumentBodyProps) {
  if (!blob) return fallback;
  return (
    <AppErrorBoundary fallback={fallback}>
      <Suspense fallback={<LoadingSpinner />}>
        <DocxView blob={blob} fileId={fileId} name={name} fallback={fallback} placeholder={<LoadingSpinner />} />
      </Suspense>
    </AppErrorBoundary>
  );
}

interface KindBodyProps {
  item: ViewerItem;
  url: string;
  blob?: Blob;
  fallback: ReactNode;
}

/** Excel, PowerPoint and kinds this build doesn't know have no in-page preview: the file card with Download. */
function KindBody({ item, url, blob, fallback }: KindBodyProps): ReactNode {
  switch (item.kind) {
    case 'image':
      return <ImageView src={url} name={item.name} fallback={fallback} />;
    case 'video':
      return <VideoView src={url} name={item.name} fallback={fallback} />;
    case 'pdf':
      return <PdfView src={url} blob={blob} name={item.name} fallback={fallback} />;
    case 'document':
      return <DocumentBody fileId={item.fileId} name={item.name} blob={blob} fallback={fallback} />;
    case 'spreadsheet':
    case 'presentation':
    default:
      return fallback;
  }
}

interface ViewerBodyProps {
  item: ViewerItem;
  file: FileSourceResult;
}

function ViewerBody({ item, file }: ViewerBodyProps) {
  if (file.isLoading) return <LoadingSpinner />;
  if (file.isError) return <FileLoadFailedCard item={item} onRetry={file.retry} />;
  const card = <FileCard item={item} />;
  if (!file.url) return card;
  return <KindBody item={item} url={file.url} blob={file.blob} fallback={card} />;
}

interface ViewerLayoutProps {
  item: ViewerItem;
  file: FileSourceResult;
  onGoToFrame?: () => void;
}

/** `Annotated frame · 0:56` for a moment; a file's name, and its size when known. */
function captionOf(item: ViewerItem): string {
  if (item.caption) return item.caption;
  if (!hasKnownSize(item.size)) return item.name;
  return `${item.name}${CAPTION_SEPARATOR}${formatBytes(item.size)}`;
}

interface PrimaryActionProps {
  file: FileSourceResult;
  downloadName: string;
  onGoToFrame?: () => void;
}

/** A moment's frame leads with Go to frame; a file with Download. */
function PrimaryAction({ file, downloadName, onGoToFrame }: PrimaryActionProps) {
  if (!onGoToFrame) return <DownloadLink url={file.url} name={downloadName} className={PRIMARY_PILL_CLASS} />;
  return (
    <button type="button" onClick={onGoToFrame} className={PRIMARY_PILL_CLASS}>
      {GO_TO_FRAME_LABEL}
    </button>
  );
}

function ViewerLayout({ item, file, onGoToFrame }: ViewerLayoutProps) {
  const downloadName = downloadNameOf(item, file);
  const caption = captionOf(item);
  return (
    <>
      <DialogTitle className="sr-only">{item.name}</DialogTitle>
      <div className={BODY_CLASS}>
        <ViewerBody item={item} file={file} />
      </div>
      <p className={CAPTION_CLASS} title={caption}>
        {caption}
      </p>
      <div className={ACTIONS_CLASS}>
        <PrimaryAction file={file} downloadName={downloadName} onGoToFrame={onGoToFrame} />
        <DialogClose asChild>
          <button type="button" className={SECONDARY_PILL_CLASS}>
            {CLOSE_LABEL}
          </button>
        </DialogClose>
      </div>
    </>
  );
}

interface ViewerPaneProps {
  item: ViewerItem;
  onGoToFrame?: () => void;
}

/** The one `useFile` call per open item; a local preview short-circuits only what's shown. */
function FetchedViewerPane({ item, fileSource, onGoToFrame }: ViewerPaneProps & { fileSource: FileSource }) {
  const fetchId = fetchableFileId(item.fileId, item.localUrl);
  const fetched = fileSource.useFile(fetchId);
  return <ViewerLayout item={item} file={viewerFile(item, fetchId, fetched)} onGoToFrame={onGoToFrame} />;
}

function ViewerPane({ fileSource, ...pane }: ViewerPaneProps & { fileSource?: FileSource }) {
  if (!fileSource) return <ViewerLayout {...pane} file={localFile(pane.item)} />;
  return <FetchedViewerPane {...pane} fileSource={fileSource} />;
}

interface StepButtonProps {
  target: number | null;
  label: string;
  StepIcon: Icon;
  className: string;
  onStep: (index: number) => void;
}

function StepButton({ target, label, StepIcon, className, onStep }: StepButtonProps) {
  function handleStep(): void {
    if (target !== null) onStep(target);
  }
  return (
    <button type="button" onClick={handleStep} disabled={target === null} aria-label={label} className={cn(STEP_BUTTON_CLASS, className)}>
      <StepIcon size={STEP_ICON_PX} weight="bold" aria-hidden />
    </button>
  );
}

export function FileViewerDialog({ items, index, onIndexChange, fileSource, onGoToFrame }: FileViewerDialogProps) {
  const item = itemAt(items, index);
  const previous = stepFrom(index, PREVIOUS_STEP, items.length);
  const next = stepFrom(index, NEXT_STEP, items.length);
  const stepByKey: Record<string, number | null> = { [ARROW_LEFT_KEY]: previous, [ARROW_RIGHT_KEY]: next };

  function handleOpenChange(open: boolean): void {
    if (!open) onIndexChange(null);
  }

  // On the dialog itself, not a window listener: Radix keeps focus inside it while it's open.
  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    if (isModifiedKey(event) || isMediaElement(event.target)) return;
    const target = stepByKey[event.key] ?? null;
    if (target === null) return;
    event.preventDefault();
    onIndexChange(target);
  }

  return (
    <Dialog open={!!item} onOpenChange={handleOpenChange}>
      <DialogContent
        showCloseButton={false}
        overlayClassName={SCRIM_CLASS}
        aria-describedby={undefined}
        onKeyDown={handleKeyDown}
        className={CONTENT_CLASS}
        data-testid={VIEWER_TESTID}
      >
        {item ? <ViewerPane key={index} item={item} fileSource={fileSource} onGoToFrame={onGoToFrame} /> : null}
        {hasSeveral(items) ? (
          <>
            <StepButton target={previous} label={PREVIOUS_FILE_LABEL} StepIcon={CaretLeft} className={PREVIOUS_BUTTON_CLASS} onStep={onIndexChange} />
            <StepButton target={next} label={NEXT_FILE_LABEL} StepIcon={CaretRight} className={NEXT_BUTTON_CLASS} onStep={onIndexChange} />
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
